import { zodResolver } from '@hookform/resolvers/zod';
import { FormProvider, useForm } from 'react-hook-form';
import { ErrorMessage } from '#components/atoms/forms/index.ts';
import {
  CONFIG_OVERRIDE_LABELS,
  LOGGING_FORMAT_LABELS,
} from '#components/patterns/config-overrides/labels/ConfigOverrideLabels.ts';
import { SelectItem } from '#components/patterns/forms/select/select-item/SelectItem.tsx';
import { SelectControlled } from '#components/patterns/forms/select/select/SelectControlled.tsx';
import type { ConfigOverride } from '#dn-types/host.ts';
import { configOverridesDefaultValues } from '#schemas/config-override/config-overrides-default-values.ts';
import { LOGGING_FORMAT } from '#schemas/config-override/helpers/enums.ts';
import { schemas, type OverrideFormData } from './form-data.ts';
import { SingleConfigForm } from './parts/SingleConfigForm.tsx';

const OVERRIDE_KEY = 'logging.format';
const DEFAULT_VALUES = { logging: { format: configOverridesDefaultValues.logging.format } };
const schema = schemas[OVERRIDE_KEY];
type FormData = OverrideFormData[typeof OVERRIDE_KEY];

type Props = {
  defaultValues?: FormData;
  onSave: (overrides: ConfigOverride[]) => void;
};

export function LoggingFormat({ defaultValues, onSave }: Props) {
  const formContext = useForm({
    resolver: zodResolver(schema),
    defaultValues: defaultValues ?? DEFAULT_VALUES,
  });

  const error = formContext.formState.errors.logging?.format;

  return (
    <FormProvider {...formContext}>
      <SingleConfigForm overrideKey={OVERRIDE_KEY} onSave={onSave} noLabel>
        <SelectControlled
          label={CONFIG_OVERRIDE_LABELS[OVERRIDE_KEY]}
          name={OVERRIDE_KEY}
          control={formContext.control}
          hasError={Boolean(error)}
          fieldChildren={error && <ErrorMessage>{error.message?.toString()}</ErrorMessage>}
          renderValue={(item) => (item ? LOGGING_FORMAT_LABELS[item] : null)}
          renderOptions={({ onChange }) => (
            <>
              {LOGGING_FORMAT.map((v) => (
                <SelectItem key={v} value={v} onClick={() => onChange(v)}>
                  {LOGGING_FORMAT_LABELS[v]}
                </SelectItem>
              ))}
            </>
          )}
        />
      </SingleConfigForm>
    </FormProvider>
  );
}
